import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useWallet } from '../contexts/WalletContext';
import { apiService } from '../services/api';
import { DonationPage } from '../types';

const DonationForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { connected, publicKey, connect, signAndSendTransaction } = useWallet();
  
  const [page, setPage] = useState<DonationPage | null>(null);
  const [amount, setAmount] = useState<number>(0);
  const [message, setMessage] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<boolean>(false);

  useEffect(() => {
    if (id) {
      loadPage();
    }
  }, [id]);

  const loadPage = async () => {
    if (!id) return;

    try {
      const response = await apiService.getDonationPage(id);
      if (response.success && response.data) {
        setPage(response.data);
        setAmount(response.data.min_amount);
      } else {
        setError('Donation page not found');
      }
    } catch (err) {
      setError('Failed to load donation page');
      console.error('Error loading page:', err);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!connected || !publicKey || !page) {
      setError('Please connect your wallet first');
      return;
    }

    if (amount < page.min_amount) {
      setError(`Minimum donation amount is ${page.min_amount} SOL`);
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Create and send Solana transaction
      const txHash = await signAndSendTransaction(page.public_key, amount);
      
      if (txHash) {
        // Send donation data to backend
        const response = await apiService.createDonation(page.id, {
          amount: amount,
          message: message,
          from_address: publicKey.toString(),
          tx_hash: txHash,
          status: 'confirmed'
        });

        if (response.success) {
          setSuccess(true);
          // Redirect to success page after 3 seconds
          setTimeout(() => {
            navigate('/');
          }, 3000);
        } else {
          setError('Failed to record donation');
        }
      }
    } catch (err) {
      setError('Transaction failed. Please try again.');
      console.error('Error processing donation:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleConnect = async () => {
    try {
      await connect();
    } catch (error) {
      console.error('Failed to connect wallet:', error);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center max-w-md mx-auto">
          <div className="text-6xl mb-6">🎉</div>
          <h2 className="text-3xl font-bold mb-4 text-green-400">Donation Successful!</h2>
          <p className="text-xl text-gray-300 mb-8">
            Thank you for your donation of {amount} SOL!
          </p>
          <p className="text-gray-400">
            Redirecting to homepage...
          </p>
        </div>
      </div>
    );
  }

  if (!page) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="spinner mx-auto mb-4"></div>
          <p className="text-xl text-gray-300">Loading donation page...</p>
        </div>
      </div>
    );
  }

  if (error && !page) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="text-6xl mb-6">❌</div>
          <h2 className="text-3xl font-bold mb-4 text-red-400">Error</h2>
          <p className="text-xl text-gray-300 mb-8">{error}</p>
          <button
            onClick={() => navigate('/')}
            className="btn-primary btn-lg"
          >
            Go Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ paddingTop: '48px', paddingBottom: '32px' }}>
      <div className="max-w-2xl mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-8" style={{ marginTop: '16px' }}>
          <h1 className="text-4xl font-bold mb-4">
            {page.description || 'Donation Page'}
          </h1>
          <p className="text-xl text-gray-300">
            Send a donation to support this streamer
          </p>
        </div>

        {/* Donation Form */}
        <div className="card">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label htmlFor="amount" className="block text-lg font-semibold text-white mb-3">
                Donation Amount (SOL)
              </label>
              <input
                type="number"
                id="amount"
                step="0.001"
                min={page.min_amount}
                value={amount}
                onChange={(e) => setAmount(parseFloat(e.target.value))}
                className="input-field text-lg"
                placeholder={`Minimum: ${page.min_amount} SOL`}
                required
              />
              <p className="text-sm text-gray-400 mt-2">
                Minimum donation: {page.min_amount} SOL
              </p>
            </div>

            <div>
              <label htmlFor="message" className="block text-lg font-semibold text-white mb-3">
                Message (Optional)
              </label>
              <textarea
                id="message"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={3}
                className="input-field text-lg resize-none"
                placeholder="Leave a message for the streamer..."
                maxLength={200}
              />
              <p className="text-sm text-gray-400 mt-2">
                {message.length}/200 characters
              </p>
            </div>

            {error && (
              <div className="bg-red-500/20 border border-red-500/50 rounded-lg p-4">
                <p className="text-red-400">{error}</p>
              </div>
            )}

            {!connected ? (
              <button
                type="button"
                onClick={handleConnect}
                className="btn-primary w-full"
              >
                Connect Wallet to Donate
              </button>
            ) : (
              <button
                type="submit"
                disabled={loading}
                className="btn-primary w-full"
              >
                {loading ? (
                  <div className="flex items-center justify-center">
                    <div className="spinner mr-2"></div>
                    Processing Donation...
                  </div>
                ) : (
                  `Donate ${amount} SOL`
                )}
              </button>
            )}
          </form>
        </div>

        {/* Info */}
        <div className="mt-8 card">
          <h3 className="text-xl font-bold text-white mb-4">ℹ️ Donation Info</h3>
          <div className="space-y-3 text-gray-300">
            <div className="flex justify-between">
              <span>Recipient:</span>
              <span className="font-mono text-sm">
                {page.public_key.slice(0, 8)}...{page.public_key.slice(-8)}
              </span>
            </div>
            <div className="flex justify-between">
              <span>Minimum Amount:</span>
              <span className="text-purple-400 font-semibold">
                {page.min_amount} SOL
              </span>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-8 text-center">
          <p className="text-sm text-gray-500">
            Powered by{' '}
            <a 
              href="/" 
              className="gradient-text hover:opacity-80 transition-opacity"
            >
              <span className="streamlab-text">Stream<span className="lab">Lab</span></span>
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default DonationForm;